import {
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";

import { api } from "./client";
import { queryKeys } from "./keys";
import type {
  CreateNutrientInput,
  ListResponse,
  Nutrient,
  UpdateNutrientInput,
} from "./types";

const base = "/v1/nutrients";

export function useNutrients() {
  return useQuery({
    queryKey: queryKeys.nutrients.list(),
    queryFn: ({ signal }) =>
      api.get<ListResponse<Nutrient>>(base, undefined, signal),
  });
}

export function useNutrient(id: string | undefined) {
  return useQuery({
    queryKey: id ? queryKeys.nutrients.detail(id) : queryKeys.nutrients.all,
    queryFn: ({ signal }) =>
      api.get<Nutrient>(`${base}/${id}`, undefined, signal),
    enabled: !!id,
  });
}

export function useCreateNutrient() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: CreateNutrientInput) =>
      api.post<Nutrient>(base, input),
    onSuccess: (created) => {
      qc.invalidateQueries({ queryKey: queryKeys.nutrients.all });
      qc.setQueryData(queryKeys.nutrients.detail(created.id), created);
    },
  });
}

export function useUpdateNutrient(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: UpdateNutrientInput) =>
      api.patch<Nutrient>(`${base}/${id}`, input),
    onSuccess: (updated) => {
      qc.setQueryData(queryKeys.nutrients.detail(id), updated);
      qc.invalidateQueries({ queryKey: queryKeys.nutrients.all });
    },
  });
}

export function useArchiveNutrient(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (reason: string | undefined) =>
      api.delete<void>(`${base}/${id}`, { reason: reason ?? "" }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: queryKeys.nutrients.all });
    },
  });
}
